import {
  marshalDateRange,
  unmarshalDateRange
} from 'v1/helpers/byType/dateHelper';
import type {
  DateRangeValue,
  FieldCodec
} from 'v5/core/entity-values/values.types';
import { customFieldColumns } from './customFieldColumns';
import { findCustomFieldRowForRead } from './customFieldRows';
import { formatStoredDate, parseStoredDate } from './storedDate';
import { validateField, validation } from './validation';

function parseBoundary(raw: string | null | undefined): Date | null {
  return raw ? parseStoredDate(raw) : null;
}

function formatBoundary(date: Date | null): string {
  return date === null ? '' : formatStoredDate(date);
}

function isEmpty(value: DateRangeValue): boolean {
  return value.from === null && value.to === null;
}

function isInvalidDate(date: Date | null): boolean {
  return date !== null && Number.isNaN(date.getTime());
}

/** DATE_RANGE custom field, marshalled as `START$END` in `string_value`. */
export const dateRangeCodec: FieldCodec<'date-range'> = {
  decode(entity, spec, ctx) {
    const row = findCustomFieldRowForRead(entity, spec.definition.id, ctx);
    const [start, end] = unmarshalDateRange(row?.string_value ?? '');
    return { from: parseBoundary(start), to: parseBoundary(end) };
  },
  encode(value) {
    return {
      type: 'custom',
      columns: customFieldColumns({
        string_value: marshalDateRange({
          start: formatBoundary(value.from),
          end: formatBoundary(value.to)
        })
      })
    };
  },
  empty() {
    return { from: null, to: null };
  },
  isEmpty,
  validate(value, spec) {
    return validateField(value, spec, isEmpty, value => {
      if (isInvalidDate(value.from) || isInvalidDate(value.to)) {
        return validation.invalid({ code: 'invalid-date-range' });
      }
      if (
        value.from !== null &&
        value.to !== null &&
        value.from.getTime() > value.to.getTime()
      ) {
        return validation.invalid({ code: 'date-range-order' });
      }
      return validation.valid();
    });
  }
};
